import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Assessments } from "@/lib/api";

const REL = [["pre", "Antes do tratamento"], ["post", "Depois do tratamento"], ["different", "Consulta diferente"], ["unknown", "Relação desconhecida"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—");
const SEG = { feet: "Pés", knees: "Joelhos", pelvis: "Bacia", spine: "Coluna", spine_lateral: "Curvas laterais", shoulders: "Ombros", cervical: "Cervical", scapula: "Omoplatas" };
const r = (v) => (v == null ? "—" : Math.round(v));

function Deltas({ before, after }) {
  if (!before || !after) return null;
  const keys = Object.keys(after.segments || {}).filter((k) => before.segments?.[k] != null && after.segments[k] != null);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs" data-testid="session-deltas">
      <div className="rounded-lg bg-slate-950/50 px-3 py-2"><div className="text-slate-500">Global</div><div className="font-mono text-slate-200">{r(before.global_score)} → {r(after.global_score)}</div></div>
      {keys.map((k) => (
        <div key={k} className="rounded-lg bg-slate-950/50 px-3 py-2"><div className="text-slate-500">{SEG[k] || k}</div><div className="font-mono text-slate-200">{r(before.segments[k])} → {r(after.segments[k])}</div></div>
      ))}
    </div>
  );
}

// Contexto da sessão: o MIO sugere, o osteopata confirma. Valores calculados nunca são alterados.
export default function SessionContextPanel({ assessmentId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ relation: "unknown", pair_id: "", therapist_note: "" });
  const load = (d) => { setData(d); setForm({ relation: d.context.relation || "unknown", pair_id: d.context.pair_id || "", therapist_note: d.context.therapist_note || "", note_shared: !!d.context.note_shared }); };
  useEffect(() => { Assessments.sessionContext(assessmentId).then(load).catch(() => {}); }, [assessmentId]);
  if (!data) return null;
  const paired = ["pre", "post"].includes(form.relation);
  const options = data.same_day.length ? data.same_day : data.pair ? [data.pair] : [];
  const save = async () => {
    try { load(await Assessments.setSessionContext(assessmentId, { ...form, pair_id: paired ? form.pair_id : null })); toast.success("Contexto da sessão guardado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao guardar"); }
  };
  const before = form.relation === "pre" ? data.current : data.pair;
  const after = form.relation === "pre" ? data.pair : data.current;
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-4" data-testid="session-context-panel">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-violet-300 font-semibold">Contexto da sessão</div>
        <p className="mt-1 text-sm text-slate-400">Indique se esta avaliação foi feita antes ou depois da intervenção, na mesma consulta.</p>
      </div>
      {data.suggestion && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 flex flex-wrap items-center gap-2" data-testid="session-suggestion">
          <span>Sugestão: {data.suggestion.reason} ({fmt(data.suggestion.created_at)})</span>
          <button type="button" className="underline" onClick={() => setForm({ ...form, relation: data.suggestion.relation, pair_id: data.suggestion.pair_id })} data-testid="session-suggestion-apply">Usar sugestão</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {REL.map(([v, l]) => (
          <button key={v} type="button" onClick={() => setForm({ ...form, relation: v })} data-testid={`session-relation-${v}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${form.relation === v ? "border-sky-500/60 bg-sky-500/10 text-sky-200" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{l}</button>
        ))}
      </div>
      {paired && (
        <label className="block text-xs text-slate-400">Avaliação correspondente (mesmo paciente)
          <select value={form.pair_id} onChange={(e) => setForm({ ...form, pair_id: e.target.value })} data-testid="session-pair-select"
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100">
            <option value="">— escolher —</option>
            {options.map((o) => <option key={o.id} value={o.id}>{fmt(o.created_at)} · global {r(o.global_score)}</option>)}
          </select>
          {!options.length && <span className="mt-1 block text-amber-300">Não há outra avaliação deste paciente no mesmo dia.</span>}
        </label>
      )}
      {paired && data.pair && form.pair_id === data.pair.id && <Deltas before={before} after={after} />}
      <label className="block text-xs text-slate-400">Interpretação profissional da resposta funcional imediata
        <textarea rows={3} value={form.therapist_note} onChange={(e) => setForm({ ...form, therapist_note: e.target.value })} data-testid="session-note"
          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Registo do osteopata (privado, salvo autorização abaixo)" />
      </label>
      {paired && (
        <label className="inline-flex items-center gap-2 text-xs text-slate-300">
          <input type="checkbox" checked={form.note_shared} onChange={(e) => setForm({ ...form, note_shared: e.target.checked })} className="h-4 w-4 accent-sky-400" data-testid="session-note-shared" />
          Partilhar esta interpretação com o paciente (relatório PDF)
        </label>
      )}
      {paired && <p className="text-[11px] text-slate-500">Com o par confirmado, o PDF inclui automaticamente a comparação antes/depois.</p>}
      <p className="text-[11px] text-slate-500 leading-relaxed">{data.caveat}</p>
      <button type="button" onClick={save} className="rounded-full bg-sky-500 px-4 py-2 text-xs font-medium text-slate-950 hover:bg-sky-400" data-testid="session-save">Guardar contexto</button>
    </section>
  );
}
